import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import Loader from "../../../../components/loader/Loader";
import PrimeBadge from "../../../../components/primeBadge/PrimeBadge";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import Button from "../../../../components/button/Button";
import {
  fetchTournamentsList,
  fetchTournamentComments,
  setSelectedTournament,
  setSearchQuery,
} from "../../../../features/tournamentAllComments/tournamentAllCommentsSlice";
import { showConfirmationModal } from "../../../../features/confirmationModal/ConfirmationModalSlice";
import styles from "./allTournamentComments.module.css";

const AllTournamentComments = () => {
  const dispatch = useDispatch();
  const {
    tournaments,
    tournamentsPage,
    tournamentsHasMore,
    tournamentsStatus,
    comments,
    currentPage,
    hasMore,
    fetchStatus,
    actionStatus,
    selectedTournamentId,
    searchQuery,
  } = useSelector((state) => state.tournamentAllComments);
  const { rolePermissions } = useSelector((state) => state.user);
  const can = (permission) => rolePermissions.includes(permission);

  const tournamentsPanelRef = useRef();
  const commentsPanelRef = useRef();
  const prevActionStatus = useRef(actionStatus);
  const searchTimerRef = useRef(null);
  const [localSearch, setLocalSearch] = useState("");

  // Fetch tournaments list on mount
  useEffect(() => {
    if (tournamentsStatus === "idle") {
      dispatch(fetchTournamentsList({ page: 1 }));
    }
  }, [dispatch, tournamentsStatus]);

  // Fetch comments when tournament or search changes
  useEffect(() => {
    if (selectedTournamentId) {
      dispatch(
        fetchTournamentComments({
          tournamentId: selectedTournamentId,
          search: searchQuery,
          page: 1,
        }),
      );
    }
  }, [dispatch, selectedTournamentId, searchQuery]);

  // Refresh after successful actions
  useEffect(() => {
    if (
      prevActionStatus.current === "loading" &&
      actionStatus === "succeeded"
    ) {
      dispatch(fetchTournamentsList({ page: 1 }));
      if (selectedTournamentId) {
        dispatch(
          fetchTournamentComments({
            tournamentId: selectedTournamentId,
            search: searchQuery,
            page: 1,
          }),
        );
      }
    }
    prevActionStatus.current = actionStatus;
  }, [dispatch, actionStatus, selectedTournamentId, searchQuery]);

  // Infinite scroll — tournaments panel
  const handleTournamentsScroll = useCallback(() => {
    const container = tournamentsPanelRef.current;
    if (!container || tournamentsStatus === "loading" || !tournamentsHasMore)
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      dispatch(fetchTournamentsList({ page: tournamentsPage + 1 }));
    }
  }, [dispatch, tournamentsStatus, tournamentsHasMore, tournamentsPage]);

  useEffect(() => {
    const container = tournamentsPanelRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleTournamentsScroll);
    return () =>
      container.removeEventListener("scroll", handleTournamentsScroll);
  }, [handleTournamentsScroll]);

  // Infinite scroll — comments panel
  const handleCommentsScroll = useCallback(() => {
    const container = commentsPanelRef.current;
    if (!container || fetchStatus === "loading" || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore && currentPage) {
      dispatch(
        fetchTournamentComments({
          tournamentId: selectedTournamentId,
          search: searchQuery,
          page: currentPage + 1,
        }),
      );
    }
  }, [dispatch, fetchStatus, hasMore, currentPage, selectedTournamentId, searchQuery]);

  useEffect(() => {
    const container = commentsPanelRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleCommentsScroll);
    return () => container.removeEventListener("scroll", handleCommentsScroll);
  }, [handleCommentsScroll]);

  // Tournament selection
  const handleTournamentSelect = (tournamentId) => {
    dispatch(setSelectedTournament(tournamentId));
    setLocalSearch("");
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearchQuery(value));
    }, 400);
  };

  // Actions
  const handleDeleteComment = (comment) => {
    dispatch(
      showConfirmationModal({
        message: "هل أنت متأكد من حذف هذا التعليق؟",
        actionType: "deleteCommentDirect",
        data: { commentId: comment._id },
      }),
    );
  };

  const handleDeleteAllUserComments = (comment) => {
    const userId = comment.userId || comment.user?._id;
    const tournamentId = comment.tournamentId || comment.tournament?._id;
    const userName = comment.user?.name || comment.user?.userName || "المستخدم";
    dispatch(
      showConfirmationModal({
        message: `هل أنت متأكد من حذف جميع تعليقات ${userName} في هذه البطولة؟`,
        actionType: "deleteAllUserComments",
        data: { userId, tournamentId },
      }),
    );
  };

  const handleBanUser = (comment) => {
    const userId = comment.userId || comment.user?._id;
    const tournamentId = comment.tournamentId || comment.tournament?._id;
    const userName = comment.user?.name || comment.user?.userName || "المستخدم";
    dispatch(
      showConfirmationModal({
        message: `هل أنت متأكد من حظر ${userName} من التعليق في هذه البطولة؟`,
        actionType: "banUserFromCommenting",
        data: { userId, tournamentId },
      }),
    );
  };

  const selectedTournamentName =
    tournaments.find(
      (t) => String(t.tournamentId) === String(selectedTournamentId),
    )?.name || "البطولة";

  return (
    <div className={styles.container}>
      <TextWrapper textStyle="textTitle">جميع تعليقات البطولات</TextWrapper>
      <div className={styles.displayContainerWrapper}>
        <DisplayContainer>
          <div className={styles.contentWrapper}>
            {/* Left Panel — Tournaments */}
            <div className={styles.tournamentsPanel} ref={tournamentsPanelRef}>
              <div className={styles.tournamentsPanelTitle}>
                <TextWrapper textStyle="textMid">البطولات</TextWrapper>
              </div>

              {tournaments.map((tournament) => (
                <div
                  key={tournament.tournamentId}
                  className={`${styles.tournamentItem} ${
                    selectedTournamentId === String(tournament.tournamentId)
                      ? styles.active
                      : ""
                  }`}
                  onClick={() =>
                    handleTournamentSelect(String(tournament.tournamentId))
                  }
                >
                  <TextWrapper textStyle="textSmall">
                    {tournament.name ||
                      `بطولة ${String(tournament.tournamentId).slice(0, 8)}...`}
                  </TextWrapper>
                  <div className={styles.tournamentItemCount}>
                    <TextWrapper textStyle="textXSmall">
                      {tournament.commentsCount}
                    </TextWrapper>
                  </div>
                </div>
              ))}

              {tournamentsStatus === "loading" && (
                <div className={styles.loaderContainer}>
                  <Loader size={25} color="var(--color-text-primary)" />
                </div>
              )}

              {tournamentsStatus === "succeeded" && tournaments.length === 0 && (
                <TextWrapper textStyle="textSmall">لا توجد بطولات</TextWrapper>
              )}
            </div>

            {/* Right Panel — Comments */}
            <div className={styles.commentsPanel} ref={commentsPanelRef}>
              {!selectedTournamentId ? (
                <div className={styles.emptyState}>
                  <TextWrapper textStyle="textMid">
                    اختر بطولة لعرض التعليقات
                  </TextWrapper>
                </div>
              ) : (
                <>
                  <div className={styles.commentsPanelHeader}>
                    <TextWrapper textStyle="textMid">
                      {selectedTournamentName}
                    </TextWrapper>
                    <TextWrapper textStyle="textXSmall">
                      {comments.length} تعليق
                    </TextWrapper>
                  </div>

                  {/* Search bar */}
                  <div className={styles.searchBar}>
                    <input
                      type="text"
                      placeholder="ابحث عن تعليق أو مستخدم..."
                      value={localSearch}
                      onChange={handleSearchChange}
                      className={styles.searchInput}
                    />
                  </div>

                  {comments.length === 0 && fetchStatus === "succeeded" && (
                    <div className={styles.emptyState}>
                      <TextWrapper textStyle="textMid">
                        لا توجد تعليقات
                      </TextWrapper>
                    </div>
                  )}

                  {comments.map((comment) => (
                    <div key={comment._id} className={styles.commentCard}>
                      {/* Header: author + date */}
                      <div className={styles.commentCardHeader}>
                        <div className={styles.commentAuthor}>
                          <TextWrapper textStyle="textSmall">
                            {comment.user?.name ||
                              comment.user?.userName ||
                              "مستخدم غير معروف"}
                          </TextWrapper>
                          <PrimeBadge
                            isPrime={comment.user?.isPrime}
                            primeTier={comment.user?.primeTier}
                            size="small"
                          />
                          {comment.isHidden && (
                            <span className={styles.hiddenBadge}>مخفي</span>
                          )}
                        </div>
                        <div className={styles.commentDate}>
                          <TextWrapper textStyle="textXSmall">
                            {new Date(comment.createdAt).toLocaleDateString(
                              "ar-SA",
                            )}
                          </TextWrapper>
                        </div>
                      </div>

                      {/* Comment text */}
                      <div className={styles.commentText}>
                        <TextWrapper textStyle="textSmall">
                          {comment.text || "لا يوجد نص"}
                        </TextWrapper>
                      </div>

                      {/* Actions */}
                      <div className={styles.commentCardActions}>
                        {can("delete-comments") && (
                          <Button
                            variant="redVariant"
                            size="midSize"
                            functionallity={() => handleDeleteComment(comment)}
                            disabled={actionStatus === "loading"}
                          >
                            <TextWrapper textStyle="textSmall">
                              حذف التعليق
                            </TextWrapper>
                          </Button>
                        )}
                        {can("delete-user-comments") && (
                          <Button
                            variant="redVariant"
                            size="midSize"
                            functionallity={() =>
                              handleDeleteAllUserComments(comment)
                            }
                            disabled={actionStatus === "loading"}
                          >
                            <TextWrapper textStyle="textSmall">
                              حذف جميع تعليقات المستخدم
                            </TextWrapper>
                          </Button>
                        )}
                        {can("ban-commenters") && (
                          <Button
                            variant="blackVariant"
                            size="midSize"
                            functionallity={() => handleBanUser(comment)}
                            disabled={actionStatus === "loading"}
                          >
                            <TextWrapper textStyle="textSmall">
                              حظر المستخدم من التعليق
                            </TextWrapper>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  {fetchStatus === "loading" && (
                    <div className={styles.loaderContainer}>
                      <Loader size={30} color="var(--color-text-primary)" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllTournamentComments;
